// Zero-knowledge client-side crypto (AES-GCM 256) via native Web Crypto API.
// The decryption key NEVER leaves the browser and is NEVER sent to the backend.

// Format: crypto_version = "aes-gcm-v1"
//   - key : AES-GCM 256 bits (32 bytes)
//   - iv  : random 12-byte nonce (AES-GCM recommendation)
//   - payload (before encryption) : JSON.stringify({ password, note })
//   - encoding : base64url without padding for ciphertext / iv / key

export const CRYPTO_VERSION = "aes-gcm-v1";

export function b64urlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function b64urlDecode(str) {
  const pad = str.length % 4 === 0 ? "" : "=".repeat(4 - (str.length % 4));
  const bin = atob(str.replace(/-/g, "+").replace(/_/g, "/") + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// Encrypt {password, note} locally. Returns the data to POST + the raw key
// (base64url) which must be placed in the URL fragment only.
export async function encryptSecret({ password, note = null }) {
  const key = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify({ password, note }));
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  const rawKey = await window.crypto.subtle.exportKey("raw", key);
  return {
    ciphertext: b64urlEncode(ct),
    iv: b64urlEncode(iv),
    crypto_version: CRYPTO_VERSION,
    key: b64urlEncode(rawKey),
  };
}

// ---------------- Secure Request (RSA-OAEP + AES-GCM hybride) "secure-request-v1" ----------------
export const SECURE_REQUEST_VERSION = "secure-request-v1";

// Demandeur: génère la paire RSA, chiffre la clé privée (JWK) avec une clé AES de wrap.
// Renvoie ce qui va au backend + la clé de wrap (fragment de l'URL privée, jamais envoyée).
export async function createSecureRequestKeys() {
  const kp = await window.crypto.subtle.generateKey(
    { name: "RSA-OAEP", modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" },
    true,
    ["encrypt", "decrypt"]
  );
  const publicJwk = await window.crypto.subtle.exportKey("jwk", kp.publicKey);
  const privateJwk = await window.crypto.subtle.exportKey("jwk", kp.privateKey);
  const wrapKey = await window.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(privateJwk));
  const enc = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, wrapKey, data);
  const rawWrap = await window.crypto.subtle.exportKey("raw", wrapKey);
  return {
    public_key_jwk: publicJwk,
    encrypted_private_key: b64urlEncode(enc),
    private_key_iv: b64urlEncode(iv),
    crypto_version: SECURE_REQUEST_VERSION,
    wrap_key: b64urlEncode(rawWrap),
  };
}

// Répondant: chiffre le secret en AES-GCM et wrappe la clé AES avec la clé publique RSA.
export async function encryptResponse(publicJwk, plaintext) {
  const pub = await window.crypto.subtle.importKey(
    "jwk", publicJwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]
  );
  const aesKey = await window.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt"]);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ct = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, new TextEncoder().encode(plaintext));
  const rawAes = await window.crypto.subtle.exportKey("raw", aesKey);
  const wrapped = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, pub, rawAes);
  return {
    ciphertext: b64urlEncode(ct), iv: b64urlEncode(iv),
    wrapped_key: b64urlEncode(wrapped), crypto_version: SECURE_REQUEST_VERSION,
  };
}

// Demandeur: déchiffre la clé privée avec la clé de wrap (fragment), unwrap la clé AES, déchiffre.
export async function decryptResponse({ ciphertext, iv, wrapped_key, encrypted_private_key, private_key_iv, wrap_key }) {
  const wrapKey = await window.crypto.subtle.importKey(
    "raw", b64urlDecode(wrap_key), { name: "AES-GCM" }, false, ["decrypt"]
  );
  const privJwkBytes = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64urlDecode(private_key_iv) }, wrapKey, b64urlDecode(encrypted_private_key)
  );
  const privateJwk = JSON.parse(new TextDecoder().decode(privJwkBytes));
  const privKey = await window.crypto.subtle.importKey(
    "jwk", privateJwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]
  );
  const rawAes = await window.crypto.subtle.decrypt({ name: "RSA-OAEP" }, privKey, b64urlDecode(wrapped_key));
  const aesKey = await window.crypto.subtle.importKey("raw", rawAes, { name: "AES-GCM" }, false, ["decrypt"]);
  const pt = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: b64urlDecode(iv) }, aesKey, b64urlDecode(ciphertext));
  return new TextDecoder().decode(pt);
}

// Destinataire: déchiffre {password, note} avec la clé AES (fragment de l'URL).
export async function decryptSecret({ ciphertext, iv, key }) {
  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    b64urlDecode(key),
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );
  const plaintext = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64urlDecode(iv) },
    cryptoKey,
    b64urlDecode(ciphertext)
  );
  const obj = JSON.parse(new TextDecoder().decode(plaintext));
  return { password: obj.password, note: obj.note ?? null };
}


// ================= Secure Delivery SMS V3 (code oral 9 chiffres) =================
// SMS = lien uniquement. CODE ORAL = 9 chiffres (~29,9 bits) communiqué séparément.
// K_secret (AES-256) chiffre le secret ; le code dérive (Argon2id -> HKDF) K_wrap (enveloppe K_secret)
// et K_verify (preuve serveur). Le backend ne reçoit jamais le code, ni K_wrap, ni la clé racine.
export const SMS_V3_VERSION = "sms-delivery-v3";
export const SMS_V3_ARGON2 = { parallelism: 1, iterations: 2, memorySize: 19456, hashLength: 32 };
const SMS_V3_WRAP_INFO = "securepass-sms-v3-wrap";
const SMS_V3_VERIFY_INFO = "securepass-sms-v3-verify";
export const ORAL_CODE_RE = /^\d{9}$/;

// Code oral : exactement 9 chiffres indépendants (CSPRNG, rejection sampling, zéros initiaux possibles).
export function generateOralCode() {
  const max = Math.floor(0xffffffff / 10) * 10;
  const buf = new Uint32Array(1);
  let out = "";
  while (out.length < 9) {
    window.crypto.getRandomValues(buf);
    if (buf[0] >= max) continue;
    out += String(buf[0] % 10);
  }
  return out;
}

export function normalizeOralCode(input) {
  return (input || "").replace(/\D/g, "").slice(0, 9);
}

export function formatOralCode(code) {
  return normalizeOralCode(code).replace(/(\d{3})(?=\d)/g, "$1 ");
}

async function argon2Root(code, saltBytes, params) {
  const { argon2id } = await import("hash-wasm");
  const bin = await argon2id({
    password: code,
    salt: saltBytes,
    parallelism: params.parallelism,
    iterations: params.iterations,
    memorySize: params.memorySize,
    hashLength: params.hashLength,
    outputType: "binary",
  });
  return new Uint8Array(bin);
}

async function hkdfSub(rootBytes, info, bits = 256) {
  const root = await window.crypto.subtle.importKey("raw", rootBytes, "HKDF", false, ["deriveBits"]);
  return new Uint8Array(await window.crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt: new Uint8Array(0), info: new TextEncoder().encode(info) },
    root, bits
  ));
}

// Dérive K_wrap (CryptoKey AES-GCM non extractible) et le verifier client (base64url de K_verify).
export async function deriveSmsV3Keys(code, saltB64, params = SMS_V3_ARGON2) {
  if (!ORAL_CODE_RE.test(code)) throw new Error("invalid_code_format");
  const root = await argon2Root(code, b64urlDecode(saltB64), params);
  const wrapBits = await hkdfSub(root, SMS_V3_WRAP_INFO, 256);
  const verifyBits = await hkdfSub(root, SMS_V3_VERIFY_INFO, 256);
  root.fill(0);
  const kWrap = await window.crypto.subtle.importKey("raw", wrapBits, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  wrapBits.fill(0);
  return { kWrap, verifier: b64urlEncode(verifyBits) };
}

// Expéditeur : chiffre {password,note}, génère le code, enveloppe K_secret avec K_wrap.
// Retourne le code (affiché, jamais envoyé), le verifier client et le payload à POSTer.
export async function encryptForSmsDeliveryV3({ password, note = null }) {
  const kSecret = await window.crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ct = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv }, kSecret, new TextEncoder().encode(JSON.stringify({ password, note }))
  );
  const code = generateOralCode();
  const saltB64 = b64urlEncode(window.crypto.getRandomValues(new Uint8Array(16)));
  const { kWrap, verifier } = await deriveSmsV3Keys(code, saltB64, SMS_V3_ARGON2);
  const wrapIv = window.crypto.getRandomValues(new Uint8Array(12));
  const rawK = new Uint8Array(await window.crypto.subtle.exportKey("raw", kSecret));
  const wrapped = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv: wrapIv }, kWrap, rawK);
  rawK.fill(0);
  return {
    code,
    payload: {
      ciphertext: b64urlEncode(ct),
      iv: b64urlEncode(iv),
      wrapped_secret_key: b64urlEncode(wrapped),
      wrapping_iv: b64urlEncode(wrapIv),
      salt: saltB64,
      argon2_params: SMS_V3_ARGON2,
      verifier_client: verifier,
      crypto_version: SMS_V3_VERSION,
    },
  };
}

// Destinataire : avec le wrapped material libéré par le serveur APRÈS unlock, récupère K_secret.
export async function unwrapSmsV3(kWrap, { wrapped_secret_key, wrapping_iv }) {
  const rawK = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64urlDecode(wrapping_iv) }, kWrap, b64urlDecode(wrapped_secret_key)
  );
  return window.crypto.subtle.importKey("raw", rawK, { name: "AES-GCM" }, false, ["decrypt"]);
}

// Destinataire : déchiffre le ciphertext (obtenu au reveal) avec K_secret déverrouillée localement.
export async function decryptWithKey(kSecretKey, { ciphertext, iv }) {
  const plaintext = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: b64urlDecode(iv) }, kSecretKey, b64urlDecode(ciphertext)
  );
  const obj = JSON.parse(new TextDecoder().decode(plaintext));
  return { password: obj.password, note: obj.note ?? null };
}
